import { useEffect, useRef, useCallback } from 'react';

/**
 * Adaptive polling for live status.
 *
 * Why this exists: the app polled running jobs every 1-2 seconds from several
 * places at once. Measured against the running backend, a single idle tab with
 * one RUNNING job in the list generated 27 req/min, and with the job details
 * modal open it generated 87 req/min. The organization's API budget is 1000
 * requests/hour (~16.7/min) shared by every member, so one person watching a
 * backup exhausted the allowance for the whole team in about 11 minutes and
 * everyone else started receiving 429s.
 *
 * Three behaviours keep the cost proportional to how much is actually changing:
 *
 *  1. Backoff — poll at `baseIntervalMs` while the payload keeps changing, and
 *     stretch toward `maxIntervalMs` once consecutive responses are identical.
 *     Progress bars stay responsive during an active transfer and cost almost
 *     nothing when a job is stalled.
 *  2. Visibility — never poll a hidden tab. Returning to the tab triggers an
 *     immediate refresh so the UI is never stale on screen.
 *  3. Overlap guard — a slow response can outlast the interval; we skip the
 *     tick rather than queueing parallel requests.
 *
 * The interval is intentionally NOT recreated on every response. The previous
 * implementation had `useEffect(..., [jobs])`, so each poll response tore down
 * and rebuilt the timer, which reset any backoff and caused timer churn.
 */

export interface AdaptivePollOptions<T> {
  /** Called on each tick and on visibility resume. Resolve with the new value. */
  fetcher: () => Promise<T>;
  /** Poll interval while data keeps changing. */
  baseIntervalMs?: number;
  /** Ceiling once responses stop changing. */
  maxIntervalMs?: number;
  /** Multiply the interval by this each time a response is unchanged. */
  backoffFactor?: number;
  /** Compare consecutive results to decide whether to back off. */
  isEqual?: (prev: T, next: T) => boolean;
  /** Set false to suspend polling (e.g. nothing is running). */
  enabled: boolean;
  /** Pause while the tab is hidden. Defaults to true. */
  pauseWhenHidden?: boolean;
}

export interface AdaptivePoll {
  /** Fetch immediately and reset the cadence to the base interval. */
  refresh: () => Promise<void>;
}

const defaultIsEqual = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/**
 * Polls `fetcher` with backoff and visibility awareness.
 *
 * Returns `refresh` for imperative callers (filter changes, manual refresh) —
 * it resets the backoff and fetches immediately.
 */
export function useAdaptivePoll<T>({
  fetcher,
  baseIntervalMs = 5000,
  maxIntervalMs = 30000,
  backoffFactor = 1.6,
  isEqual = defaultIsEqual,
  enabled,
  pauseWhenHidden = true,
}: AdaptivePollOptions<T>): AdaptivePoll {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef(false);
  const currentIntervalRef = useRef(baseIntervalMs);
  const lastValueRef = useRef<T | null>(null);
  const hasValueRef = useRef(false);

  // Keep the latest fetcher without restarting the loop when it changes identity
  // (callers usually pass an inline arrow).
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const isEqualRef = useRef(isEqual);
  isEqualRef.current = isEqual;

  const clear = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const runOnce = useCallback(async (): Promise<void> => {
    if (inFlightRef.current) return; // overlap guard
    inFlightRef.current = true;
    try {
      const next = await fetcherRef.current();
      if (hasValueRef.current && isEqualRef.current(lastValueRef.current as T, next)) {
        currentIntervalRef.current = Math.min(
          maxIntervalMs,
          Math.round(currentIntervalRef.current * backoffFactor)
        );
      } else {
        currentIntervalRef.current = baseIntervalMs;
      }
      lastValueRef.current = next;
      hasValueRef.current = true;
    } catch {
      // Transient failure (including 429): ease off rather than hammering.
      currentIntervalRef.current = Math.min(
        maxIntervalMs,
        Math.round(currentIntervalRef.current * backoffFactor)
      );
    } finally {
      inFlightRef.current = false;
    }
  }, [baseIntervalMs, maxIntervalMs, backoffFactor]);

  /** Imperative refresh: fetch now and reset to the base interval. */
  const refresh = useCallback(async (): Promise<void> => {
    currentIntervalRef.current = baseIntervalMs;
    await runOnce();
  }, [baseIntervalMs, runOnce]);

  useEffect(() => {
    if (!enabled) {
      clear();
      return;
    }

    let cancelled = false;

    const schedule = () => {
      clear();
      timerRef.current = setTimeout(async () => {
        if (cancelled) return;
        if (pauseWhenHidden && document.hidden) {
          schedule(); // stay parked while hidden
          return;
        }
        await runOnce();
        if (!cancelled) schedule();
      }, currentIntervalRef.current);
    };

    // Start from a clean cadence whenever polling is (re-)enabled.
    currentIntervalRef.current = baseIntervalMs;
    void runOnce().then(() => {
      if (!cancelled) schedule();
    });

    const onVisibility = () => {
      if (cancelled) return;
      if (!document.hidden) {
        // Returning to the tab: refresh immediately so nothing is stale on screen.
        currentIntervalRef.current = baseIntervalMs;
        void runOnce().then(() => {
          if (!cancelled) schedule();
        });
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      clear();
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // `enabled` and the cadence knobs define the loop; the fetcher is read via ref.
  }, [enabled, baseIntervalMs, maxIntervalMs, pauseWhenHidden, runOnce, clear]);

  return { refresh };
}
